"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import StoryboardPage from "@/components/tools/games-organizers/storyboard-page";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  type Aspect,
  bestGrid,
  DEFAULT_FRAMES,
  DEFAULT_LINES,
  MAX_FRAMES,
  MAX_LINES,
  MIN_FRAMES,
  MIN_LINES,
} from "@/lib/tool-kit/games-organizers/storyboard";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
} from "@/lib/tool-kit/printables/page";

/** English strings. A translated content file overrides these through `ui`. */
export const STORYBOARD_TEMPLATE_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  titleLabel: "Title (optional)",
  titlePlaceholder: "Storyboard: The water cycle",
  framesLabel: "Frames",
  aspectLabel: "Frame shape",
  aspects: { "16:9": "16:9 widescreen", "4:3": "4:3 standard" },
  linesLabel: "Caption lines under each frame",
  numbered: "Number the frames",
  nameLine: "Name and date line",
  nameLabel: "Name",
  dateLabel: "Date",
  previewLabel: "Storyboard page",
} as const;

const FRAME_CHOICES = Array.from(
  { length: MAX_FRAMES - MIN_FRAMES + 1 },
  (_, i) => MIN_FRAMES + i
);
const LINE_CHOICES = Array.from(
  { length: MAX_LINES - MIN_LINES + 1 },
  (_, i) => MIN_LINES + i
);

export default function StoryboardTemplateWidget() {
  const strings = useToolUi(STORYBOARD_TEMPLATE_STRINGS);
  const fmt = useToolNumber();
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [title, setTitle] = useState("");
  const [frames, setFrames] = useState(DEFAULT_FRAMES);
  const [aspect, setAspect] = useState<Aspect>("16:9");
  const [lines, setLines] = useState(DEFAULT_LINES);
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [orientation, setOrientation] = useState<Orientation>("landscape");
  const [numbered, setNumbered] = useState(true);
  const [nameLine, setNameLine] = useState(true);

  const geometry = pageGeometry(paper, orientation);
  const grid = bestGrid(frames, aspect, geometry, lines);

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <TextField
          id="sb-title"
          label={strings.titleLabel}
          onChange={setTitle}
          placeholder={strings.titlePlaceholder}
          value={title}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            id="sb-frames"
            label={strings.framesLabel}
            onChange={(value) => setFrames(Number(value))}
            options={FRAME_CHOICES.map((n) => ({
              value: String(n),
              label: fmt(n),
            }))}
            value={String(frames)}
          />
          <SelectField
            id="sb-lines"
            label={strings.linesLabel}
            onChange={(value) => setLines(Number(value))}
            options={LINE_CHOICES.map((n) => ({
              value: String(n),
              label: fmt(n),
            }))}
            value={String(lines)}
          />
          <PaperControls
            idPrefix="sb"
            onOrientation={setOrientation}
            onPaper={setPaper}
            orientation={orientation}
            paper={paper}
            strings={strings}
          />
        </div>
        <PillGroup
          label={strings.aspectLabel}
          name="sb-aspect"
          onChange={setAspect}
          options={(["16:9", "4:3"] as const).map((value) => ({
            value,
            label: strings.aspects[value],
          }))}
          value={aspect}
        />
        <fieldset className="space-y-2">
          <Checkbox
            checked={numbered}
            label={strings.numbered}
            onChange={setNumbered}
          />
          <Checkbox
            checked={nameLine}
            label={strings.nameLine}
            onChange={setNameLine}
          />
        </fieldset>
        <PrintBar
          fileName={`storyboard-${paper}`}
          pageTotal={1}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
      </div>
      <PrintablePages
        className={orientation === "landscape" ? "max-w-4xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={[
          <StoryboardPage
            dateLabel={strings.dateLabel}
            geometry={geometry}
            grid={grid}
            key="storyboard"
            measure={measure}
            nameLabel={strings.nameLabel}
            nameLine={nameLine}
            numbered={numbered}
            title={title.trim()}
          />,
        ]}
        svgRef={svgRef}
      />
    </div>
  );
}
